import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';

import { useLoaderStore } from '@/stores/useLoaderStore';
import { fetchGetLookupSuburbGnaf } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface SuburbsSearchViewDataComposable {
  isFetchingSuburbs: Readonly<Ref<boolean>>;
  suburbs: Readonly<
    Ref<
      | ReadonlyDeep<openapi.AddressBookGnafSuburbLookupListContentOpenapi>
      | undefined
    >
  >;
  refreshSuburbs: () => Promise<void>;
}

export default function useSuburbsSearchViewData(
  querySuburb: Readonly<Ref<string | undefined>>,
  queryPostCode: Readonly<Ref<number | undefined>>,
): SuburbsSearchViewDataComposable {
  const route = useRoute();

  const loaderStore = useLoaderStore();

  let suburbsController: AbortController | undefined;

  const suburbsFetchCounter = ref(0);
  const isFetchingSuburbs = computed(() => !!suburbsFetchCounter.value);
  const suburbs =
    ref<ReadonlyDeep<openapi.AddressBookGnafSuburbLookupListContentOpenapi>>();

  function clear(): void {
    if (suburbsController) suburbsController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchSuburbs(): Promise<
    | ReadonlyDeep<openapi.AddressBookGnafSuburbLookupListContentOpenapi>
    | undefined
  > {
    if (suburbsController) suburbsController.abort();
    const suburbGuess = querySuburb.value;
    const postCode = queryPostCode.value;
    if (!suburbGuess && !postCode?.toString()) return;
    suburbsController = new AbortController();
    return await fetchGetLookupSuburbGnaf(
      {
        suburbGuess,
        postCode,
      },
      {
        signal: suburbsController.signal,
      },
    )
      .then(function (data) {
        return data;
      })
      .catch(function (err: unknown) {
        if (!isCancel(err)) {
          throw err;
        }
        return undefined;
      });
  }

  async function refreshSuburbs(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    suburbsFetchCounter.value++;
    try {
      suburbs.value = await _fetchSuburbs();
    } finally {
      suburbsFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  watch(
    [querySuburb, queryPostCode, route],
    async function (newValues, oldValues) {
      if (route.name !== routeNames.SEARCH_SUBURBS) return;
      await refreshSuburbs();
    },
    { immediate: true },
  );

  return {
    isFetchingSuburbs,
    suburbs,
    refreshSuburbs,
  };
}
